/**
 * 오른쪽 패널의 **한 화면** — SQL 실행이력 · 쿼리 챌린지 중 하나(SQL 실행계획 분석은 2026-10-26 편집기 실행계획에 합쳤다).
 *
 * ## 왜 탭이 아닌가 (사용자 요청 2026-09-20)
 *
 * 예전에는 패널을 연 뒤 **그 안에서 탭을 한 번 더** 골라야 했다. 그래서 「무엇이 열려 있나」를
 * 알려면 두 곳(툴바의 패널 토글 + 패널 안의 탭)을 봐야 했고, 여는 데도 두 번 눌러야 했다.
 * 지금은 편집기 툴바의 버튼이 곧 화면을 정한다 — 눌린 버튼 하나가 답이다.
 *
 * 그래서 이 컴포넌트는 **상태를 갖지 않는다.** 무엇을 그릴지는 `view` 로 받는다.
 * (챌린지만 예외로 다시 열 때 새로 마운트한다 — 아래 `remountKey` 참조.)
 *
 * ## 제목줄은 공통 컴포넌트다
 *
 * 같은 레일에 나란히 서는 패널이라 제목줄이 서로 달라 보이면 한 앱의 화면으로 안 읽힌다.
 * 모양의 단일 정의는 `common/PanelHeader` 다 — 여기서 또 칠하지 말 것.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SQLHistory from './SQLHistory';
import QueryChallenge from './QueryChallenge';
import { FiClock, FiAward } from 'react-icons/fi';
import PanelHeader from './common/PanelHeader';
import './HistoryAndChallengeTabs.css';

/** 화면마다의 아이콘·제목. 툴바 버튼과 **같은 문구**를 쓴다(한 기능이 두 이름으로 보이지 않게). */
const VIEW_META = {
  history: { icon: FiClock, titleKey: 'common:hct.history' },
  challenge: { icon: FiAward, titleKey: 'common:hct.challenge' },
};

const HistoryAndChallengeTabs = ({
  view = 'history', onClose,
  history, onHistoryClick, clearHistory, deleteHistoryItem,
  externalActiveChallengeSubTab,
  challengeMode = 'query',
  challengeDraft = null,
  onChallengeDraftConsumed,
  onShowChallengePlans,
  // 창 조작(새 창으로 분리 · 전체 화면) — 편집기 자리에 그릴 때 DetachableView 가 준다(2026-10-19)
  headerControls = null,
  // 창 틀(DetachableView)이 제목줄을 그릴 때는 이 패널의 제목줄을 두지 않는다(2026-10-20 — 두 겹이 되지 않게)
  hideHeader = false
}) => {
  const { t } = useTranslation();

  // 챌린지는 열 때마다 새로 마운트한다 — 목록·참여 기록을 다시 읽게 하려는 것이다.
  // (예전 탭 시절의 `queryChallengeKey` 와 같은 뜻이고, 이제 여는 일이 곧 탭 클릭이다.)
  const [remountKey, setRemountKey] = useState(0);
  useEffect(() => {
    if (view === 'challenge') setRemountKey(k => k + 1);
  }, [view]);

  const meta = VIEW_META[view] || VIEW_META.history;
  const Icon = meta.icon;

  return (
    <div className="history-challenge-tabs-outer">
      {/* 제목줄은 **공통 컴포넌트**다 — 같은 레일의 다른 패널과 모양이 갈리지 않게 */}
      {!hideHeader && <PanelHeader icon={<Icon size={13} />} title={view === 'challenge' && challengeMode === 'tuning' ? t('menu:challenge.tuning') : t(meta.titleKey)} onClose={onClose}>{headerControls}</PanelHeader>}

      <div className="history-challenge-tabs-content">
        {view === 'history' && (
          <SQLHistory
            history={history}
            onHistoryClick={onHistoryClick}
            clearHistory={clearHistory}
            deleteHistoryItem={deleteHistoryItem}
          />
        )}
        {view === 'challenge' && (
          <div className="challenge-dark-wrapper">
            {/* 쿼리챌린지 ↔ 튜닝챌린지를 오갈 때는 **키에 모드를 넣어** 그 렌더에서 바로 새로 마운트한다(2026-10-06).
                effect 로 키를 올리면 한 렌더 늦어, 옛 패널이 새 모드로 한 번 그려지며 쿼리챌린지의 선택을 튜닝 쪽 「고른
                챌린지」 기억에 써 버렸다 — 튜닝챌린지로 넘어가도 쿼리챌린지 문항이 그대로 남았다(신고 2026-10-06) */}
            <QueryChallenge key={`${challengeMode}-${remountKey}`} mode={challengeMode} onShowPlans={onShowChallengePlans}
              draft={challengeDraft} onDraftConsumed={onChallengeDraftConsumed} />
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryAndChallengeTabs;
